import { getExaminersThunk } from '../../../../store/workspace/thunks';
import { setExamStatsTab } from '../../../../store/workspace/slice';
import { ExamStatsTab } from '../../../../store/workspace/types';
import { useDispatch, useSelector } from '../../../../store/store';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { buildExamTabs } from '../components/ExamTabs';
import { EXAM_DETAIL_TABS } from '../constants';
import {
    selectExamStatsTab,
    selectCodingExamStats,
    selectExamStatsJobInfo,
    selectMultipleChoiceExamStats,
    selectFetchingExaminersDetail,
    selectRecruitingInterviewStatists,
} from '../../../../store/workspace/selectors';

/**
 * Shared plumbing of the exam sheet and exam result screens: both read the same
 * three exam payloads, expose the same tabs, and lazily fetch the tab the user
 * lands on.
 */
export function useExamDetail() {
    const dispatch = useDispatch();
    const { jobId } = useParams();

    const tab = useSelector(selectExamStatsTab);
    const jobInfo = useSelector(selectExamStatsJobInfo);
    const fetching = useSelector(selectFetchingExaminersDetail);
    const codingExam = useSelector(selectCodingExamStats);
    const multipleChoiceExam = useSelector(selectMultipleChoiceExamStats);
    const recruitingInterview = useSelector(selectRecruitingInterviewStatists);

    const isCurrentTabLoaded =
        (tab === 'coding' && Boolean(codingExam)) ||
        (tab === 'recruitingInterview' && Boolean(recruitingInterview)) ||
        (tab === 'multipleChoice' && Boolean(multipleChoiceExam));

    useEffect(() => {
        if (!jobId) return;
        if (tab && isCurrentTabLoaded) return;

        dispatch(getExaminersThunk({ jobId, tab }));
        // Re-evaluated on tab changes only: the cached payloads never disappear
        // while the screen is mounted.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const changeTab = useCallback((nextTab: ExamStatsTab) => {
        dispatch(setExamStatsTab(nextTab));
    }, [dispatch]);

    const tabs = useMemo(() => buildExamTabs(jobInfo, EXAM_DETAIL_TABS), [jobInfo]);

    return {
        tab,
        tabs,
        jobInfo,
        fetching,
        changeTab,
        codingExam,
        multipleChoiceExam,
        recruitingInterview,
    };
};
